import React from 'react';
import {
  AddToCalendarLink,
  H3,
  Paragraph,
  StackedColumn,
  StackedColumnSpacing,
} from '../../../core';
import {
  CernerAppointmentDetails,
  SchedulingDetails,
  formatPatientName,
} from '../../../api/models';
import { AddressUtility, DateUtility, PhoneUtility } from '@mchs/web-core';

interface DateAndTimeProps {
  appointmentDetails: CernerAppointmentDetails;
  schedulingDetails: SchedulingDetails;
}

const reschedule1 = 'If you need to ';
const reschedule2 = 'reschedule or cancel ';
const reschedule3 = "this appointment, please call the provider's office.";
const rescheduleFull = reschedule1 + reschedule2 + reschedule3;

const makeDateTimeReadable = (dateTime: string): string => {
  const dateTimeAsDate: Date =
    DateUtility.parseDateISOYearMonthDayTime(dateTime);
  return DateUtility.formatDateLongWeekdayLongMonthAtTime(dateTimeAsDate);
};

const formatAppointmentDescription = (
  appointmentDetails: CernerAppointmentDetails,
  schedulingDetails: SchedulingDetails
): string => {
  const details: string[] = [
    `Provider: ${appointmentDetails.providerName}`,
    `${PhoneUtility.formatPhoneNumber(
      PhoneUtility.cleanPhone(appointmentDetails.facilityPhoneNumber ?? '')
    )}`,
    '',
    `Patient: ${formatPatientName(
      schedulingDetails.patientDetails?.demographics
    )}`,
    '',
    `Reason for appointment: ${appointmentDetails.description}`,
    '',
    'Location',
    appointmentDetails.facilityName ?? '',
    appointmentDetails.streetAddress ?? '',
    `${appointmentDetails.city}, ${appointmentDetails.state} ${appointmentDetails.zipCode}`,
    '',
    `Time: ${makeDateTimeReadable(appointmentDetails.startDateTime ?? '')}`,
    `Check In Time: ${appointmentDetails.requiredCheckInTime}`,
    rescheduleFull,
    '',
  ];

  if (
    appointmentDetails.reminderInstructionsV2 &&
    appointmentDetails.reminderInstructionsV2.length > 0
  ) {
    details.push('Instructions');
    appointmentDetails.reminderInstructionsV2.forEach((instruction) => {
      details.push(instruction.title ?? '');
      details.push(instruction.text ?? '');
      details.push('');
    });
  }

  return details.join('\n');
};

const DateAndTime: React.FC<DateAndTimeProps> = (props: DateAndTimeProps) => {
  return (
    <StackedColumn spacing={StackedColumnSpacing.NoGap}>
      <H3 text="DATE AND TIME" />
      <Paragraph
        text={makeDateTimeReadable(
          props.appointmentDetails.startDateTime ?? ''
        )}
      />
      <Paragraph
        text={`Check In Time: ${props.appointmentDetails.requiredCheckInTime}`}
      />
      <div>
        <Paragraph text={reschedule1} />
        <Paragraph text={reschedule2} bold={true} />
        <Paragraph text={reschedule3} />
      </div>
      <div>
        <AddToCalendarLink
          calendarEvent={{
            title: 'Doctor appointment',
            description: formatAppointmentDescription(
              props.appointmentDetails,
              props.schedulingDetails
            ),
            startDate: DateUtility.parseDateISOYearMonthDayTime(
              props.appointmentDetails.startDateTime ?? '0000-00-00T00:00:00'
            ),
            endDate: props.appointmentDetails.endDateTime
              ? DateUtility.parseDateISOYearMonthDayTime(
                  props.appointmentDetails.endDateTime
                )
              : undefined,
            durationInMinutes: undefined,
            address: AddressUtility.formatAddress(
              props.appointmentDetails.zipCode ?? '',
              props.appointmentDetails.streetAddress,
              props.appointmentDetails.city,
              props.appointmentDetails.state
            ),
          }}
        />
      </div>
    </StackedColumn>
  );
};

export default DateAndTime;
